import { listSuburbs } from "@/api/Suburbs";
import {
  listRequests,
  RequestModel,
  listRequestTypes,
  RequestTypeDto,
  createRequest,
  cancelRequest,
  listWasteStreams,
  listBinSizes,
  listMoreInformationTypes,
  listDeadAnimalSizes,
  listCancelReasons,
  listPremiseTypes
} from "@/api/Requests";
import { getAddressByAssessmentNumber, AddressByAssessmentNumberModel, AddressBin } from "@/api/AddressLookup";
import { LoadingStore } from "./loadingStore";
import { action, computed, observable, reaction } from "mobx";
import format from "date-fns/format";
import { DATE_PICKER_FORMAT } from "@/Constants";
import { SuburbAndStreetsModel, WasteStreamModel, WasteStreamBinSize } from "@/api/Shared";
import userStore from "@/store/userStore";
import notificationStore from "./notificationStore";
import { UnsuccessfulReasonDto } from "@/api/Requests";
import { equalsIgnoringCase, containsIgnoringCase } from "@/helpers/stringHelper";
import { postRequestExport, PostRequestExportRequest } from "@/api/Requests/postRequestExport";

export class RequestFilters {
  constructor() {
    this.dateRange = [format(new Date(), DATE_PICKER_FORMAT), format(new Date(), DATE_PICKER_FORMAT)];
  }

  @observable dateRange: string[] = [];
  @observable suburbs: SuburbAndStreetsModel[] = [];
  @observable wasteStreams: WasteStreamModel[] = [];
  @observable streets: string[] = [];
  @observable includeInProgress = true;
  @observable includeComplete = false;
  @observable includePending = false;
  @observable referenceNumber = "";
  @observable requestTypes: string[] = [];
}

export class CreateRequestViewModel {
  @observable firstName = "";
  @observable lastName = "";
  @observable phoneNumber = "";
  @observable email = "";
  @observable unitNumber = "";
  @observable streetNumber = "";
  @observable streetName = "";
  @observable suburb = "";
  @observable postcode = "";
  @observable state = "";
  @observable assessmentNumber = "";
  @observable requestType = "";
  @observable moreInformation: string[] = [];
  @observable taskType = "";
  @observable deadAnimalSize = "";
  @observable animalType = "";
  @observable wasteStreams: WasteStreamBinSize[] = [];
  @observable requestNotes = "";
  @observable addressBins: AddressBin[] = [];
}

export class CancelRequestViewModel {
  @observable requestId = "";
  @observable cancelReason = "";
  @observable cancelNotes = "";
}

export class RequestStore {
  @observable requests: RequestModel[] = [];
  @observable filters = new RequestFilters();
  @observable createRequestVm = new CreateRequestViewModel();
  @observable cancelRequestVm = new CancelRequestViewModel();
  @observable addressFound: AddressByAssessmentNumberModel | undefined;
  @observable RequestCancelDialogOpen = false;
  @observable AddressFoundDialogOpen = false;
  @observable suburbs: SuburbAndStreetsModel[] = [];
  @observable wasteStreams: WasteStreamModel[] = [];
  @observable wasteStreamNames: string[] = [];
  @observable drsProWasteStreams: WasteStreamBinSize[] = [];
  @observable drsBinSizes: string[] = [];
  @observable premiseTypes: string[] = [];
  @observable currentPage = 1;
  @observable requestTypes: string[] = [];
  @observable requestTypesAndTasks: RequestTypeDto[] = [];
  @observable cancelReasons: UnsuccessfulReasonDto[] = [];
  @observable addressByAssessementNumber: AddressByAssessmentNumberModel | undefined = undefined;
  @observable allRequestsForToday = false;
  @observable moreInformationTypes: string[] = [];
  @observable deadAnimalSizes: string[] = [];
  @observable requestTaskTypes: string[] = [];
  @observable cancelItemSelected: RequestModel | null = null;
  @observable loading = new LoadingStore();

  @computed get enableCreateRequest(): boolean {
    return userStore.councilConfiguration?.enableCreateRequest ?? false;
  }

  @computed get enableExportRequest(): boolean {
    return userStore.councilConfiguration?.enableExportRequest ?? false;
  }

  @computed get enableFurtherEntries(): boolean {
    return this.requestTaskTypes.length > 0;
  }

  @computed get displayWasteStreams(): boolean {
    return (
      this.drsProWasteStreams.length > 0 &&
      !equalsIgnoringCase(this.createRequestVm.requestType, "Dead animal") &&
      !equalsIgnoringCase(this.createRequestVm.requestType, "Council Query") &&
      !equalsIgnoringCase(this.createRequestVm.requestType, "Complaint") &&
      !equalsIgnoringCase(this.createRequestVm.requestType, "Compliment")
    );
  }

  @computed get wasteStreamIsSelected(): boolean {
    return this.createRequestVm.wasteStreams.length > 0;
  }

  @computed get requestTypeIsBinMaintenance(): boolean {
    return containsIgnoringCase(this.createRequestVm.requestType, "Bin Maintenance");
  }

  @computed get requestTypeIsDeadAnimal(): boolean {
    return equalsIgnoringCase(this.createRequestVm.requestType, "Dead animal");
  }

  @computed get addressFoundTitle(): string {
    return "A Different Address Found With Assessment Number " + this.createRequestVm.assessmentNumber;
  }

  @computed get cancelReasonNames(): string[] {
    return this.cancelReasons.filter(f => f.isCommercial == this.cancelItemIsCommercial).map(x => x.name);
  }

  @computed get cancelItemIsCommercial(): boolean {
    if (this.cancelItemSelected == null) {
      return false;
    }

    if (this.cancelItemSelected.tasks == null || this.cancelItemSelected.tasks.length == 0) {
      //if there is no tasks, then use the isCommercial flag on request
      return this.cancelItemSelected.isCommercial;
    }

    console.log("is commercial: " + this.cancelItemSelected.tasks[0].isCommercial);

    return this.cancelItemSelected.tasks[0].isCommercial;
  }

  constructor() {
    reaction(
      () => this.createRequestVm.requestType,
      async () => {
        this.createRequestVm.taskType = "";
        if (this.createRequestVm.requestType) {
          this.requestTaskTypes = this.requestTypesAndTasks.find(r => r.requestType == this.createRequestVm.requestType)?.requestTaskTypes.map(rt => rt.taskTypeName) ?? [];
        } else {
          this.requestTaskTypes = [];
        }

        if (this.requestTypeIsBinMaintenance) {
          if (this.moreInformationTypes.length == 0) {
            await this.loadMoreInformationTypes();
          }
        } else {
          this.createRequestVm.moreInformation = [];
        }

        if (this.requestTypeIsDeadAnimal && this.deadAnimalSizes.length == 0) {
          await this.loadDeadAnimalSizes();
        }

        if (!this.displayWasteStreams) {
          this.createRequestVm.wasteStreams = [];
        }
      }
    );

    reaction(
      () => ({
        dateRange: this.filters.dateRange,
        suburbs: this.filters.suburbs,
        streets: this.filters.streets,
        wasteStreams: this.filters.wasteStreams,
        includeInProgress: this.filters.includeInProgress,
        includeComplete: this.filters.includeComplete,
        referenceNumber: this.filters.referenceNumber,
        requestTypes: this.filters.requestTypes
      }),
      () => {
        this.resetResults();
      }
    );

    setTimeout(() => {
      reaction(
        () => JSON.stringify(userStore.councilConfiguration),
        () => {
          this.wasteStreams = userStore.councilConfiguration?.wasteConfiguration.map(w => ({ wasteStreamId: w.id, wasteStreamName: w.name } as WasteStreamModel)) ?? [];
          this.wasteStreamNames = userStore.councilConfiguration?.wasteConfiguration.map(w => w.name) ?? [];
        },
        { fireImmediately: true }
      ),
        0;
    });
  }

  @action.bound async fetchData() {
    this.loading.track(async () => {
      try {
        this.suburbs = (await listSuburbs()).suburbs;
        this.requestTypesAndTasks = await listRequestTypes();
        this.requestTypes = this.requestTypesAndTasks.map(l => l.requestType);
        this.filters = new RequestFilters();

        if (this.allRequestsForToday) {
          this.filters.includePending = true;
          this.filters.includeComplete = true;
          this.filters.includeInProgress = true;

          this.fetchRequests();
          this.allRequestsForToday = false;
        }
        this.resetResults();
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading the request data");
      }
    }, "fetchData");
  }

  @action.bound async fetchCreateRequestData() {
    this.loading.track(async () => {
      try {
        this.resetCreateRequest();
        this.requestTypesAndTasks = await listRequestTypes();
        this.requestTypes = this.requestTypesAndTasks.map(l => l.requestType);

        this.drsProWasteStreams = (await listWasteStreams()).map(x => ({ name: x } as WasteStreamBinSize));
        this.drsBinSizes = await listBinSizes();
        this.premiseTypes = await listPremiseTypes();
        this.createRequestVm = new CreateRequestViewModel();
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading the create request data");
      }
    }, "fetchCreateRequestData");
  }

  @action.bound async loadMoreInformationTypes() {
    this.loading.track(async () => {
      try {
        this.moreInformationTypes = (await listMoreInformationTypes()) ?? [];
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading more information options");
      }
    }, "loadMoreInformationTypes");
  }

  @action.bound async loadDeadAnimalSizes() {
    this.loading.track(async () => {
      try {
        this.deadAnimalSizes = (await listDeadAnimalSizes()) ?? [];
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading dead animal sizes");
      }
    }, "loadDeadAnimalSizes");
  }

  @action.bound async getAddressByAssessmentNumber(assessmentNumber: string): Promise<AddressByAssessmentNumberModel | undefined> {
    return this.loading.track<AddressByAssessmentNumberModel | undefined>(async () => {
      try {
        const request = {
          assessmentNumber: assessmentNumber
        };

        const addressByAssessementNumberRequest = await getAddressByAssessmentNumber(request);
        const addressByAssessementNumber = addressByAssessementNumberRequest.addressInfo;

        return addressByAssessementNumber;
      } catch (error) {
        return undefined;
      }
    }, "getAddressByAssessmentNumber");
  }

  @action.bound async searchAddressByAssessmentNumber(assessmentNumber: string): Promise<boolean | undefined> {
    return this.loading.track<boolean | undefined>(async () => {
      try {
        this.addressByAssessementNumber = await this.getAddressByAssessmentNumber(assessmentNumber);

        if (this.addressByAssessementNumber) {
          this.createRequestVm.unitNumber = this.addressByAssessementNumber.unitNumber;
          this.createRequestVm.streetNumber = this.addressByAssessementNumber.streetNumber;
          this.createRequestVm.streetName = this.addressByAssessementNumber.streetName;
          this.createRequestVm.suburb = this.addressByAssessementNumber.suburb;
          this.createRequestVm.postcode = this.addressByAssessementNumber.postcode;
          this.createRequestVm.state = this.addressByAssessementNumber.state;
          this.createRequestVm.assessmentNumber = assessmentNumber;
          this.createRequestVm.addressBins = this.addressByAssessementNumber.addressBins;

          return true;
        }

        return false;
      } catch (error) {
        notificationStore.error("An unexpected error occurred while searching address by assessment number");

        return false;
      }
    }, "searchAddressByAssessmentNumber");
  }

  @action.bound async CreateRequest() {
    const request = {
      firstName: this.createRequestVm.firstName,
      lastName: this.createRequestVm.lastName,
      phoneNumber: this.createRequestVm.phoneNumber,
      email: this.createRequestVm.email,
      unitNumber: this.createRequestVm.unitNumber,
      streetNumber: this.createRequestVm.streetNumber,
      streetName: this.createRequestVm.streetName,
      suburb: this.createRequestVm.suburb,
      postcode: this.createRequestVm.postcode,
      state: userStore.councilConfiguration?.state ?? "",
      assessmentNumber: this.createRequestVm.assessmentNumber,
      requestType: this.createRequestVm.requestType,
      taskType: this.createRequestVm.taskType,
      wasteStreams: this.createRequestVm.wasteStreams,
      requestNotes: this.createRequestVm.requestNotes,
      moreInformation: this.createRequestVm.moreInformation,
      deadAnimalSize: this.createRequestVm.deadAnimalSize,
      animalType: this.createRequestVm.animalType
    };

    return this.loading.track(async () => {
      try {
        const created = (await createRequest(request)).created;
        if (created) {
          this.resetCreateRequest();
        } else {
          throw "";
        }
      } catch (error) {
        console.log(error);
        throw "";
      }
    }, "CreateRequest");
  }

  @action.bound async initializeCancelRequestData() {
    if (this.cancelReasonNames.length > 0) return;

    this.loading.track(async () => {
      try {
        this.cancelReasons = await listCancelReasons();
      } catch (error) {
        notificationStore.error("An unexpected error occurred while loading the cancel request data");
      }
    }, "initializeCancelRequestData");
  }

  @action.bound CancelRequest(requestId: string) {
    //sending cancel request
    const requestDto = {
      requestId: requestId,
      cancelReason: this.cancelReasons.find(c => c.name == this.cancelRequestVm.cancelReason),
      cancelNotes: this.cancelRequestVm.cancelNotes
    };

    return this.loading.track(async () => {
      try {
        if (await cancelRequest(requestDto)) {
          // Find the request and update its status
          const req = this.requests.find(r => r.requestId == requestId);
          if (req) {
            req.status = "CANCELLED";
            if (req.tasks && req.tasks.length > 0) {
              req.tasks.forEach(task => {
                task.status = "CANCELLED";
              });
            }
          }
          this.resetCancelRequest();
          return;
        }
        throw "";
      } catch (error) {
        console.log(error);
        throw "";
      }
    }, "CancelRequest");
  }

  @action.bound async fetchRequests() {
    const request = {
      startAt: new Date(this.filters.dateRange[0]),
      endAt: new Date(this.filters.dateRange.length === 1 ? this.filters.dateRange[0] : this.filters.dateRange[1]),
      requestTypes: this.filters.requestTypes,
      suburbs: this.filters.suburbs.map(x => x.suburbName),
      streets: this.filters.streets,
      WasteStreamIds: this.filters.wasteStreams.length > 0 ? this.filters.wasteStreams.map(ct => ct.wasteStreamId) : [],
      includeComplete: this.filters.includeComplete,
      includeInProgress: this.filters.includeInProgress,
      includePending: this.filters.includePending,
      referenceNumber: this.filters.referenceNumber
    };

    return this.loading.track(async () => {
      try {
        const response = await listRequests(request);
        this.resetResults();
        this.requests = response.requests.map(request => ({
          ...request,
          requestNotes: request.requestNotes,
          addressStreetNoDisplay: request.addressUnitNo ? request.addressUnitNo + "/" + request.addressStreetNo : request.addressStreetNo
        }));
      } catch (error) {
        console.log(error);
        notificationStore.error("An unexpected error occurred while loading the requests");
      }
    }, "fetchRequests");
  }

  @action.bound async exportToXlsx() {
    return this.loading.track(async () => {
      try {
        const request: PostRequestExportRequest = {
          startAt: new Date(this.filters.dateRange[0]),
          endAt: new Date(this.filters.dateRange.length === 1 ? this.filters.dateRange[0] : this.filters.dateRange[1]),
          requestTypes: this.filters.requestTypes,
          suburbs: this.filters.suburbs.map(x => x.suburbName),
          streets: this.filters.streets,
          wasteStreamIds: this.filters.wasteStreams.length > 0 ? this.filters.wasteStreams.map(ct => ct.wasteStreamId) : [],
          includeComplete: this.filters.includeComplete,
          includeInProgress: this.filters.includeInProgress,
          includePending: this.filters.includePending,
          referenceNumber: this.filters.referenceNumber
        };

        await postRequestExport(request);
      } catch (error) {
        console.log(error);
        notificationStore.error("An unexpected error occurred while exporting the requests");
      }
    }, "exportToXlsx");
  }

  @action.bound reset() {
    this.filters = new RequestFilters();
    this.resetResults();
  }

  @action.bound resetResults() {
    this.requests = [];
    this.currentPage = 1;

    this.resetCreateRequest();
    this.resetCancelRequest();
  }

  @action.bound resetCreateRequest() {
    this.resetCreateRequestEntries();
    this.moreInformationTypes = [];
    this.drsProWasteStreams = [];
    this.requestTaskTypes = [];
  }

  @action.bound resetCreateRequestEntries() {
    this.createRequestVm = new CreateRequestViewModel();
    this.addressByAssessementNumber = undefined;
  }

  @action.bound resetAddressFound() {
    this.AddressFoundDialogOpen = false;
    this.addressFound = undefined;
  }

  @action.bound resetCancelRequest() {
    this.cancelRequestVm = new CancelRequestViewModel();
    this.cancelReasons = [];
    this.RequestCancelDialogOpen = false;
  }
}

const requestStore = new RequestStore();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).requestStore = requestStore;

export default requestStore;
